import type { PublicConfigDto, MeDto } from '@dijitalapp/shared';
import { translatorFor, type Catalog, type Translator } from '@dijitalapp/i18n/core';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { api, ApiError } from './api';

export type ThemePreference = 'light' | 'dark' | 'system';

interface AppState {
  config: PublicConfigDto | null;
  me: MeDto | null;
  loading: boolean;
  online: boolean;
  i18n: Translator;
  theme: ThemePreference;
  setTheme: (t: ThemePreference) => void;
  setLocale: (l: 'tr' | 'en') => void;
  setMe: Dispatch<SetStateAction<MeDto | null>>;
  refreshMe: () => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

// Per-device conveniences only; never security-relevant state.
function readPref(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) — preference lasts for this tab only */
  }
}

function initialLocale(): 'tr' | 'en' {
  const saved = readPref('da.locale');
  if (saved === 'tr' || saved === 'en') return saved;
  return navigator.language.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

// Turkish is built in (it is also the fallback); English is its own file, downloaded only for people who use it.
let englishLoad: Promise<Catalog | null> | null = null;
function loadEnglish(): Promise<Catalog | null> {
  englishLoad ??= import('@dijitalapp/i18n/en').then(
    (m) => m.en,
    () => {
      englishLoad = null; // offline before it was ever cached: stay in Turkish, try again when back online
      return null;
    },
  );
  return englishLoad;
}
// Started right away, alongside the first API calls, so English speakers never see Turkish first.
if (initialLocale() === 'en') void loadEnglish();

export function AppProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PublicConfigDto | null>(null);
  const [me, setMe] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [locale, setLocaleState] = useState<'tr' | 'en'>(initialLocale);
  const [theme, setThemeState] = useState<ThemePreference>(
    // Light unless the person chose otherwise ("system" stays available in Settings > Appearance).
    () => (readPref('da.theme') as ThemePreference | null) ?? 'light',
  );

  const effectiveLocale = me?.locale ?? locale;
  const wantsEnglish = effectiveLocale.startsWith('en');
  const [english, setEnglish] = useState<Catalog | null>(null);
  const [englishFailed, setEnglishFailed] = useState(false);
  useEffect(() => {
    if (!wantsEnglish || english) return;
    let live = true;
    void loadEnglish().then((catalog) => {
      if (!live) return;
      if (catalog) setEnglish(catalog);
      else setEnglishFailed(true);
    });
    return () => {
      live = false;
    };
  }, [wantsEnglish, english, online]);
  // Until English arrives the previous language stays on screen (a switch in Settings takes a moment).
  const i18n = useMemo(() => translatorFor(effectiveLocale, english ? { en: english } : {}), [effectiveLocale, english]);
  const catalogPending = wantsEnglish && !english && !englishFailed;
  // Remember the account's language on this device, so the next start guesses right before /me answers.
  useEffect(() => {
    if (me?.locale === 'tr' || me?.locale === 'en') writePref('da.locale', me.locale);
  }, [me?.locale]);

  useEffect(() => {
    document.documentElement.lang = i18n.locale;
    document.documentElement.dir = i18n.dir;
  }, [i18n]);

  useEffect(() => {
    if (theme === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
    // The browser/installed-app title bar follows the chosen theme, not only the system one.
    const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) meta.content = dark ? '#0b1220' : '#f6f7f9';
  }, [theme]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      setMe(await api<MeDto>('GET', '/me'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setMe(null);
      else throw err;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await api<PublicConfigDto>('GET', '/config/public');
        setConfig(cfg);
        const root = document.documentElement.style;
        root.setProperty('--brand-primary', cfg.brand.colors.primary);
        root.setProperty('--brand-primary-contrast', cfg.brand.colors.primaryContrast);
        root.setProperty('--brand-secondary', cfg.brand.colors.secondary);
        document.title = cfg.brand.name;
        await refreshMe();
      } catch {
        /* leave config null: the UI shows the connection error state */
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshMe]);

  const value: AppState = {
    config,
    me,
    loading: loading || catalogPending,
    online,
    i18n,
    theme,
    setTheme: (t) => {
      setThemeState(t);
      writePref('da.theme', t);
    },
    setLocale: (l) => {
      setLocaleState(l);
      writePref('da.locale', l);
      if (me) {
        void api<MeDto>('PATCH', '/me', { locale: l }).then(setMe, () => undefined);
      }
    },
    setMe,
    refreshMe,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}
